import { useEffect, useState } from "react";
import { Star, ExternalLink } from "lucide-react";
import { Reveal, Eyebrow } from "./Reveal";
import { api } from "../lib/api";

const Stars = ({ value = 0, className = "w-4 h-4" }) => (
    <span className="inline-flex gap-0.5" aria-label={`${value} out of 5 stars`}>
        {[1, 2, 3, 4, 5].map((i) => (
            <Star
                key={i}
                className={`${className} ${i <= Math.round(value) ? "fill-brand-amber text-brand-amber" : "text-brand-line"}`}
            />
        ))}
    </span>
);

const ReviewsSection = () => {
    const [data, setData] = useState(null);

    useEffect(() => {
        api.get("/reviews").then((r) => setData(r.data)).catch(() => setData(null));
    }, []);

    if (!data?.configured || !data.reviews?.length) return null;

    return (
        <section className="border-y border-brand-line bg-brand-surface" data-testid="reviews-section">
            <div className="max-w-7xl mx-auto px-5 sm:px-8 py-24">
                <Reveal className="mb-14 flex flex-col md:flex-row md:items-end justify-between gap-6">
                    <div>
                        <Eyebrow className="mb-4">Google reviews</Eyebrow>
                        <h2 className="font-display font-black uppercase text-brand-cream text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[0.95]">
                            What neighbors <span className="text-brand-amber">say.</span>
                        </h2>
                    </div>
                    <div className="flex items-center gap-4" data-testid="reviews-summary">
                        <span className="font-display font-black text-brand-cream text-5xl leading-none">
                            {data.rating?.toFixed(1)}
                        </span>
                        <div>
                            <Stars value={data.rating} className="w-5 h-5" />
                            <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-brand-sage mt-1">
                                {data.userRatingCount} Google review{data.userRatingCount === 1 ? "" : "s"}
                            </p>
                        </div>
                    </div>
                </Reveal>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
                    {data.reviews.map((r, i) => (
                        <Reveal key={r.googleMapsUri || i} delay={(i % 3) * 0.06}>
                            <article
                                className="h-full rounded-lg border border-brand-line bg-brand-ink p-6 flex flex-col"
                                data-testid={`review-card-${i}`}
                            >
                                <div className="flex items-center justify-between mb-4">
                                    <Stars value={r.rating} />
                                    <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-brand-moss">
                                        {r.relativeTime}
                                    </span>
                                </div>
                                <p className="text-brand-sage text-sm leading-relaxed flex-1">{r.text}</p>
                                <div className="mt-5 flex items-center justify-between gap-3">
                                    <a
                                        href={r.authorUri}
                                        target="_blank"
                                        rel="noreferrer"
                                        className="flex items-center gap-2 min-w-0 text-brand-cream text-sm font-semibold hover:text-brand-amber transition-colors"
                                    >
                                        {r.authorPhoto && (
                                            <img src={r.authorPhoto} alt="" className="w-7 h-7 rounded-full shrink-0" loading="lazy" />
                                        )}
                                        <span className="truncate">{r.author}</span>
                                    </a>
                                    {r.googleMapsUri && (
                                        <a
                                            href={r.googleMapsUri}
                                            target="_blank"
                                            rel="noreferrer"
                                            aria-label="View this review on Google Maps"
                                            className="text-brand-moss hover:text-brand-amber transition-colors shrink-0"
                                        >
                                            <ExternalLink className="w-4 h-4" />
                                        </a>
                                    )}
                                </div>
                            </article>
                        </Reveal>
                    ))}
                </div>

                <Reveal className="mt-10 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                    <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-brand-moss">
                        Reviews shown in Google's relevance order · Powered by Google
                    </p>
                    {data.googleMapsUri && (
                        <a
                            href={data.googleMapsUri}
                            target="_blank"
                            rel="noreferrer"
                            data-testid="reviews-write-link"
                            className="inline-flex items-center gap-2 border border-brand-cream/30 text-brand-cream font-semibold text-sm px-6 py-3 rounded-full hover:border-brand-amber hover:text-brand-amber transition-colors"
                        >
                            Read all on Google
                            <ExternalLink className="w-4 h-4" />
                        </a>
                    )}
                </Reveal>
            </div>
        </section>
    );
};

export default ReviewsSection;
